import React, { useCallback, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Box } from '@components/Box';
import { Text } from '@components/typography';
import { Columns } from '@components/Columns';
import { ChevronRightIcon } from '@images/ChevronRight';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import {
  SectionContainer,
  TextPairingContainer,
  StyledLeftImageWrapper,
  StyledRightImageWrapper,
  ColumnWrapper,
  StyleMiddleColumn,
  StyledDesktopPrintsImage,
  StyledMobilePrintsImage,
  MobileWrapper,
  StyledExploreButton,
} from './HighQualityPrintsSection.styles';

interface ImageDataProps {
  desktopImageUrl: string;
  mobileImageUrl: string;
}

interface HighQualityPrintsSectionProps {
  highQualityPrintsSectionData: {
    eyebrow: string;
    body: string;
    title: string;
    ctaText: string;
    leftImageData: ImageDataProps;
    rightImageData: ImageDataProps;
  };
}

export const HighQualityPrintsSection: React.FC<HighQualityPrintsSectionProps> = ({ highQualityPrintsSectionData }) => {
  const sectionId = 'HighQualityPrintsSection';
  const { body, eyebrow, title, ctaText, leftImageData, rightImageData } = highQualityPrintsSectionData;

  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleMobile, setIsVisibleMobile] = useState(false);

  const handleClickExplore = useCallback(() => {
    const elmnt = document.getElementById('gallery-grid-wrapper');
    elmnt?.scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  return (
    <SectionContainer marginTop={[10, 0]} id={sectionId}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>
      <MobileWrapper>
        <LazyLoadWrapper beforeLoad={() => setIsVisibleMobile(true)}>
          <div />
        </LazyLoadWrapper>
      </MobileWrapper>
      <ColumnWrapper fluid centered>
        <Columns.Column width={3}>
          <StyledLeftImageWrapper>
            <StyledDesktopPrintsImage isMainSrcShown={isVisible} src={leftImageData?.desktopImageUrl} />
            <StyledMobilePrintsImage isMainSrcShown={isVisibleMobile} src={leftImageData?.mobileImageUrl} imagePosition="bottom" />
          </StyledLeftImageWrapper>
        </Columns.Column>
        <Columns.Column width={6}>
          <StyleMiddleColumn paddingTop={[9, 0]} paddingBottom={[2, 0]} paddingX={[6, 0]} flexDirection="column" alignItems="center">
            <TextPairingContainer
              eyebrow={eyebrow}
              eyebrowColor="humanaDark4"
              eyebrowVariant={'sectionEyeBrow20'}
              eyebrowTagName="h2"
              alignment={'center'}
              title={ReactHtmlParser(title)}
              titleVariant={'hed72Hero'}
              titleColor="neutra6"
              titleTagName="h2"
              body={ReactHtmlParser(body)}
              bodyColor="neutra6"
              bodyVariant="dek20"
            />
            <Box marginTop={[9]}>
              <StyledExploreButton role="button" onClick={handleClickExplore}>
                <Text variant="featureCopy17" style={{ fontWeight: 'bold' }} color="purple4" tagName="span">
                  {ctaText}
                </Text>
                <ChevronRightIcon />
              </StyledExploreButton>
            </Box>
          </StyleMiddleColumn>
        </Columns.Column>
        <Columns.Column width={3}>
          <StyledRightImageWrapper>
            <StyledDesktopPrintsImage isMainSrcShown={isVisible} src={rightImageData?.desktopImageUrl} />
            <StyledMobilePrintsImage isMainSrcShown={isVisibleMobile} src={rightImageData?.mobileImageUrl} imagePosition="top" />
          </StyledRightImageWrapper>
        </Columns.Column>
      </ColumnWrapper>
    </SectionContainer>
  );
};
